import { useState } from "react";
import { Label } from "@/components/ui/label";

export const ROLE_CHOICES = [
  { key: "manager", label: "Gestor" },
  { key: "supervisor", label: "Supervisor" },
  { key: "operator", label: "Operador" },
  { key: "auditor", label: "Auditor" },
];

/**
 * FASE 17/19 — shared visibility scoping picker (Todos / Por perfil / Por setor / Individual).
 * Stores the choice as arrays on the form (assigned_roles / assigned_sector_ids /
 * assigned_user_ids) so the backend can decide with plain list membership.
 */
export default function VisibilityPicker({
  form,
  setForm,
  users = [],
  sectors = [],
  testPrefix = "editor-visibility",
  description = "Quem verá esse checklist no dispositivo dos operadores.",
}) {
  const initialMode =
    (form.assigned_user_ids || []).length > 0
      ? "users"
      : (form.assigned_sector_ids || []).length > 0
      ? "sectors"
      : (form.assigned_roles || []).length > 0
      ? "roles"
      : "all";
  const [mode, setModeState] = useState(initialMode);

  const setMode = (next) => {
    setModeState(next);
    if (next === "all") {
      setForm((f) => ({ ...f, assigned_roles: [], assigned_user_ids: [], assigned_sector_ids: [] }));
    } else if (next === "roles") {
      setForm((f) => ({
        ...f,
        assigned_user_ids: [],
        assigned_sector_ids: [],
        assigned_roles: f.assigned_roles?.length ? f.assigned_roles : ["operator"],
      }));
    } else if (next === "sectors") {
      setForm((f) => ({
        ...f,
        assigned_roles: [],
        assigned_user_ids: [],
        assigned_sector_ids: f.assigned_sector_ids || [],
      }));
    } else {
      setForm((f) => ({
        ...f,
        assigned_roles: [],
        assigned_sector_ids: [],
        assigned_user_ids: f.assigned_user_ids || [],
      }));
    }
  };

  const toggleIn = (key) => (val) => {
    setForm((f) => {
      const cur = new Set(f[key] || []);
      if (cur.has(val)) cur.delete(val);
      else cur.add(val);
      return { ...f, [key]: Array.from(cur) };
    });
  };
  const toggleRole = toggleIn("assigned_roles");
  const toggleSector = toggleIn("assigned_sector_ids");
  const toggleUser = toggleIn("assigned_user_ids");

  return (
    <div className="border border-stone-200 rounded-md p-3 bg-stone-50/40" data-testid={testPrefix}>
      <Label className="font-body text-sm font-semibold text-stone-800">
        Visibilidade
      </Label>
      <p className="text-[11px] text-stone-500 font-body mt-0.5">{description}</p>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {[
          { key: "all", label: "Todos", desc: "Visível a todos" },
          { key: "roles", label: "Por perfil", desc: "Um ou mais perfis" },
          { key: "sectors", label: "Por setor", desc: "Todos do setor recebem" },
          { key: "users", label: "Individual", desc: "Usuários específicos" },
        ].map((opt) => (
          <label
            key={opt.key}
            className={`flex flex-col items-start gap-0.5 rounded-md border px-2 py-1.5 cursor-pointer transition-colors ${
              mode === opt.key
                ? "border-emerald-300 bg-emerald-50"
                : "border-stone-200 bg-white hover:border-stone-300"
            }`}
          >
            <div className="flex items-center gap-1.5">
              <input
                type="radio"
                checked={mode === opt.key}
                onChange={() => setMode(opt.key)}
                className="accent-emerald-700"
                data-testid={`${testPrefix}-${opt.key}`}
              />
              <span className="text-sm font-semibold">{opt.label}</span>
            </div>
            <span className="text-[10px] text-stone-600 font-body pl-5">{opt.desc}</span>
          </label>
        ))}
      </div>

      {mode === "roles" && (
        <div className="mt-3 space-y-1" data-testid={`${testPrefix}-roles-picker`}>
          {ROLE_CHOICES.map((r) => {
            const checked = (form.assigned_roles || []).includes(r.key);
            return (
              <label
                key={r.key}
                className="flex items-center gap-2 text-sm font-body cursor-pointer hover:bg-stone-50 px-2 py-1 rounded"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleRole(r.key)}
                  className="accent-emerald-700"
                  data-testid={`${testPrefix}-role-${r.key}`}
                />
                {r.label}
              </label>
            );
          })}
        </div>
      )}

      {mode === "sectors" && (
        <div
          className="mt-3 border border-stone-200 rounded-md bg-white max-h-56 overflow-y-auto p-1.5 space-y-0.5"
          data-testid={`${testPrefix}-sectors-picker`}
        >
          {sectors.length === 0 && (
            <div className="text-xs text-stone-500 font-body italic py-2 text-center">
              Nenhum setor cadastrado. Crie setores na página Usuários.
            </div>
          )}
          {sectors.map((s) => {
            const checked = (form.assigned_sector_ids || []).includes(s.id);
            return (
              <label
                key={s.id}
                className="flex items-center gap-2 text-sm font-body cursor-pointer hover:bg-stone-50 px-2 py-1 rounded"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleSector(s.id)}
                  className="accent-emerald-700"
                  data-testid={`${testPrefix}-sector-${s.id}`}
                />
                {s.name}
              </label>
            );
          })}
          {sectors.length > 0 && (form.assigned_sector_ids || []).length === 0 && (
            <p className="text-[11px] text-amber-700 font-body px-2 py-1 italic">
              Selecione ao menos um setor — se ficar vazio, ninguém verá.
            </p>
          )}
        </div>
      )}

      {mode === "users" && (
        <div
          className="mt-3 border border-stone-200 rounded-md bg-white max-h-56 overflow-y-auto p-1.5 space-y-0.5"
          data-testid={`${testPrefix}-users-picker`}
        >
          {users.length === 0 && (
            <div className="text-xs text-stone-500 font-body italic py-2 text-center">
              Nenhum usuário cadastrado ainda.
            </div>
          )}
          {users.map((u) => {
            const checked = (form.assigned_user_ids || []).includes(u.id);
            return (
              <label
                key={u.id}
                className="flex items-center gap-2 text-sm font-body cursor-pointer hover:bg-stone-50 px-2 py-1 rounded"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleUser(u.id)}
                  className="accent-emerald-700"
                  data-testid={`${testPrefix}-user-${u.id}`}
                />
                <span className="flex-1 truncate">{u.name || u.email}</span>
                <span className="text-[10px] text-stone-500 uppercase">{u.role}</span>
              </label>
            );
          })}
          {users.length > 0 && (form.assigned_user_ids || []).length === 0 && (
            <p className="text-[11px] text-amber-700 font-body px-2 py-1 italic">
              Selecione ao menos um usuário — se ficar vazio, ninguém verá.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
